import config from "./config";
import { guideSlugMap } from "./guideSlugMap";

/**
 * Detect language from the current URL based on the configured mode.
 *
 * Detection order:
 *  1. Dedicated domain match (domainMap) → e.g. thailandelite.fr → "fr"
 *  2. Prefix mode:   /fr/adhesions → "fr"
 *  3. Subdomain mode: fr.thailand-elite.com → "fr"
 *
 * Returns null if the URL matches the default language (no prefix/subdomain).
 */
export function detectLanguageFromURL(): string | null {
  // 1. Check if the current hostname matches a dedicated domain
  const domainLang = detectFromDomain();
  if (domainLang) return domainLang;

  if (config.mode === "subdomain") {
    return detectFromSubdomain();
  }
  return detectFromPrefix();
}

/**
 * Check if window.location.hostname matches any entry in domainMap.
 * e.g. thailandelite.fr → "fr"
 */
function detectFromDomain(): string | null {
  const domainMap = config.domainMap || {};
  const hostname = window.location.hostname.toLowerCase().replace(/\/$/, "");

  for (const [lang, domain] of Object.entries(domainMap)) {
    const domainHost = domain.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (hostname === domainHost) {
      return lang;
    }
  }
  return null;
}

function detectFromPrefix(): string | null {
  const path = window.location.pathname;
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const firstSegment = segments[0].toLowerCase();
  if (
    config.supportedLanguages.includes(firstSegment) &&
    firstSegment !== config.defaultLanguage
  ) {
    return firstSegment;
  }
  return null;
}

function detectFromSubdomain(): string | null {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  // Need at least 3 parts for a subdomain (e.g. fr.domain.com)
  if (parts.length < 3) return null;

  const subdomain = parts[0].toLowerCase();
  const map = config.subdomainMap || {};
  const lang = map[subdomain] || subdomain;

  if (
    config.supportedLanguages.includes(lang) &&
    lang !== config.defaultLanguage
  ) {
    return lang;
  }
  return null;
}

/**
 * Strip the language prefix from a pathname (prefix mode only).
 * e.g. "/fr/memberships" → "/memberships"
 */
export function stripLangPrefix(pathname: string): string {
  if (config.mode !== "prefix") return pathname;

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";

  const firstSegment = segments[0].toLowerCase();
  if (
    config.supportedLanguages.includes(firstSegment) &&
    firstSegment !== config.defaultLanguage
  ) {
    const rest = "/" + segments.slice(1).join("/");
    return rest || "/";
  }
  return pathname;
}

/**
 * Get the localized slug for a given route name and language.
 * Falls back to the route name itself if no translation is defined.
 *
 * e.g. getLocalizedSlug("memberships", "fr") → "adhesions"
 * e.g. getLocalizedSlug("memberships", "de") → "memberships"
 */
export function getLocalizedSlug(routeName: string, lang: string): string {
  const slugMap = config.slugMap || {};
  return slugMap[routeName]?.[lang] ?? routeName;
}

/**
 * Resolve a localized slug back to its English route name.
 * Used to match incoming URLs with localized slugs to React routes.
 *
 * e.g. resolveSlugToRoute("adhesions", "fr") → "memberships"
 * e.g. resolveSlugToRoute("memberships", "fr") → "memberships" (fallback)
 */
export function resolveSlugToRoute(slug: string, lang: string): string | null {
  const slugMap = config.slugMap || {};
  for (const [routeName, translations] of Object.entries(slugMap)) {
    if (translations[lang] === slug) return routeName;
  }
  // If no translation found, it's either already English or unknown
  return slug;
}

/**
 * Translate a full path to the localized version for a given language.
 * Handles both top-level slugs (from config.slugMap) and guide sub-paths.
 *
 * Returns the translated path (unchanged if no translation needed).
 *
 * e.g. translateFullPath("/memberships", "fr")            → "/adhesions"
 * e.g. translateFullPath("/guides/stay-extension", "fr")  → "/guides/extension-sejour-de-1-an"
 * e.g. translateFullPath("/guides/adhesions", "fr")       → "/guides/adhesions" (unchanged)
 */
export function translateFullPath(pathname: string, lang: string): string {
  // Separate hash
  const hashIdx = pathname.indexOf("#");
  const hash = hashIdx !== -1 ? pathname.slice(hashIdx) : "";
  const pathOnly = hashIdx !== -1 ? pathname.slice(0, hashIdx) : pathname;
  const clean = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;

  // Handle /guides/:slug sub-paths using guideSlugMap
  const guideMatch = clean.match(/^\/guides\/(.+)$/);
  if (guideMatch) {
    const guideSlug = guideMatch[1];
    // Resolve slug → guideKey (check all languages)
    for (const [, langs] of Object.entries(guideSlugMap)) {
      const values = Object.values(langs) as string[];
      // Find if current slug matches any language's slug for this guide
      if (values.includes(guideSlug)) {
        const localizedSlug = langs[lang] ?? langs["en"] ?? guideSlug;
        if (localizedSlug !== guideSlug) {
          return `/guides/${localizedSlug}${hash}`;
        }
        return clean + hash; // Already correct slug
      }
    }
    return clean + hash;
  }

  // Handle top-level slugs using config.slugMap via translatePathSlugs
  return translatePathSlugs(clean, lang);
}

/**
 * Check if the current runtime is on the dedicated domain for a given language.
 * e.g. onDedicatedDomain("fr") → true when running on thailandelite.fr
 * Returns false during SSR (no window).
 */
function onDedicatedDomain(lang: string): boolean {
  if (typeof window === "undefined") return false;
  const domainMap = config.domainMap || {};
  if (!domainMap[lang]) return false;
  const currentHost = window.location.hostname.toLowerCase();
  const dedicatedHost = domainMap[lang].toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/$/, "");
  return currentHost === dedicatedHost;
}

/**
 * Resolve a localized path back to its English canonical form.
 * The reverse of translatePathSlugs — used by LanguageSwitcher when switching languages.
 *
 * e.g. resolvePathToEnglish("/actualites", "fr")   → "/news"
 * e.g. resolvePathToEnglish("/adhesion/gold", "fr") → "/membership/gold"
 * e.g. resolvePathToEnglish("/news", "fr")          → "/news" (already English)
 */
export function resolvePathToEnglish(pathname: string, lang: string): string {
  if (lang === config.defaultLanguage) return pathname;

  const slugMap = config.slugMap || {};
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return pathname;

  for (const [routeName, translations] of Object.entries(slugMap)) {
    const localizedSlug = translations[lang];
    if (!localizedSlug) continue;

    const routeParts = localizedSlug.split("/");
    const pathParts = segments;

    if (routeParts.length !== pathParts.length) continue;

    // Check static parts match the localized slug
    const isMatch = routeParts.every((part, i) => {
      if (part.startsWith(":")) return true; // dynamic segment
      return part === pathParts[i];
    });

    if (isMatch) {
      // Reconstruct with English route name, preserving dynamic segment values
      const enParts = routeName.split("/");
      const result = enParts.map((part, i) => {
        if (part.startsWith(":")) return pathParts[i] ?? part;
        return part;
      });
      return "/" + result.join("/");
    }
  }

  return pathname;
}

/**
 * Build a localized path. Default language gets no prefix.
 * Uses localized slug if available for the target language.
 *
 * Runtime-aware: detects whether we're on the dedicated domain or main domain.
 *
 * e.g. buildLocalizedPath("/memberships", "fr")
 *   → "/adhesions"      (when running on thailandelite.fr)
 *   → "/fr/adhesions"   (when running on thailand-elite.com in prefix mode)
 * e.g. buildLocalizedPath("/memberships", "en") → "/memberships" (default language)
 */
export function buildLocalizedPath(path: string, lang: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;

  // Translate the slug portion
  const localizedPath = translatePathSlugs(clean, lang);

  if (config.mode === "subdomain") {
    return localizedPath;
  }

  // Prefix mode — default language has no prefix
  if (lang === config.defaultLanguage) {
    return localizedPath;
  }

  // For languages with a dedicated domain:
  //   - On the dedicated domain itself → no prefix (e.g. /adhesions on .fr)
  //   - On the main domain → use lang prefix (e.g. /fr/adhesions on .com)
  const domainMap = config.domainMap || {};
  if (domainMap[lang]) {
    if (onDedicatedDomain(lang)) {
      return localizedPath; // No prefix on dedicated domain
    }
    // On main domain with lang prefix: /fr/adhesions
    return `/${lang}${localizedPath}`;
  }

  // Non-dedicated-domain language: always add prefix
  return `/${lang}${localizedPath}`;
}

/**
 * Translate the first path segment (slug) to the target language.
 * Handles dynamic segments like /membership/:tier → /adhesion/:tier
 * Preserves hash fragments (e.g. /memberships#bronze → /adhesions#bronze)
 *
 * @internal
 */
function translatePathSlugs(path: string, lang: string): string {
  // Separate hash from path
  const hashIdx = path.indexOf("#");
  const hash = hashIdx !== -1 ? path.slice(hashIdx) : "";
  const pathWithoutHash = hashIdx !== -1 ? path.slice(0, hashIdx) : path;

  const slugMap = config.slugMap || {};
  const segments = pathWithoutHash.split("/").filter(Boolean);
  if (segments.length === 0) return path;

  // Try to match with dynamic param (e.g. "membership/:tier")
  for (const [routeName, translations] of Object.entries(slugMap)) {
    const localizedSlug = translations[lang] ?? routeName;
    const routeParts = routeName.split("/");
    const pathParts = segments;

    if (routeParts.length !== pathParts.length) continue;

    // Check if the static parts match the English route
    const isMatch = routeParts.every((part, i) => {
      if (part.startsWith(":")) return true; // dynamic segment
      return part === pathParts[i];
    });

    if (isMatch) {
      // Reconstruct path with localized slug, preserving dynamic segments
      const localizedParts = localizedSlug.split("/");
      const result = localizedParts.map((part, i) => {
        if (part.startsWith(":")) {
          // Replace with actual value from original path
          return pathParts[i] ?? part;
        }
        return part;
      });
      return "/" + result.join("/") + hash;
    }
  }

  return pathWithoutHash + hash;
}

/**
 * Build a full absolute URL for a given path and language.
 * Uses domainMap if the language has a dedicated domain (path rendered at root with localized slug).
 * Otherwise uses mainDomain + localized path.
 *
 * This function is domain-agnostic (safe to use in SSR/worker contexts).
 *
 * e.g. buildFullUrl("/memberships", "fr") → "https://thailandelite.fr/adhesions"
 * e.g. buildFullUrl("/memberships", "en") → "https://thailand-elite.com/memberships"
 * e.g. buildFullUrl("/memberships", "de") → "https://thailand-elite.com/de/memberships"
 */
export function buildFullUrl(path: string, lang: string): string {
  const domainMap = config.domainMap || {};
  const clean = path.startsWith("/") ? path : `/${path}`;

  // If this language has its own domain, use it with the localized path (no prefix)
  if (domainMap[lang]) {
    const domain = domainMap[lang].replace(/\/$/, "");
    // Translate slug without runtime domain check (always use dedicated domain behavior)
    const localizedPath = translateFullPath(clean, lang);
    return `${domain}${localizedPath.startsWith("/") ? localizedPath : "/" + localizedPath}`;
  }

  // Otherwise: main domain + lang prefix + localized path
  const domain = config.mainDomain.replace(/\/$/, "");
  const localizedPath = translateFullPath(clean, lang);
  if (lang === config.defaultLanguage) {
    return `${domain}${localizedPath}`;
  }
  return `${domain}/${lang}${localizedPath}`;
}

// ── Locale and site URL helpers (used by blog and other localized UI) ─────────

/** BCP-47 locale tags for Intl APIs (date formatting, number formatting, etc.) */
export const INTL_LOCALE_MAP: Record<string, string> = {
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  ja: "ja-JP",
  ru: "ru-RU",
  zh: "zh-CN",
};

/** Resolve a BCP-47 locale tag for Intl APIs from any i18n.language value. */
export function getIntlLocale(lang: string | undefined): string {
  if (!lang) return INTL_LOCALE_MAP[config.defaultLanguage] ?? "en-US";
  const base = lang.split("-")[0].toLowerCase();
  return INTL_LOCALE_MAP[base] ?? INTL_LOCALE_MAP[config.defaultLanguage] ?? "en-US";
}

/** Return the canonical site origin for a language (dedicated domain if any, else main domain). */
export function getSiteUrlForLang(lang: string | undefined): string {
  const base = (lang || config.defaultLanguage).split("-")[0].toLowerCase();
  return (config.domainMap?.[base] ?? config.mainDomain).replace(/\/$/, "");
}

/** Human-readable name for a language code, used in language switchers. */
export const LANGUAGE_NATIVE_NAMES: Record<string, string> = {
  en: "English",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  ja: "日本語",
  ru: "Русский",
  zh: "中文",
};
